import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { isAdFreeUser } from '../../utils/isAdFreeUser';

const SLOT_DIMENSIONS = {
  banner: { width: 960, height: 254 },
  sidebar: { width: 300, height: 250 },
  mobile_sticky: { width: 300, height: 110 },
};

// This campaign is also preloaded from index.html. Rendering it on the first
// React commit removes the API-gated LCP delay while the live ad response still
// remains authoritative for tracking, rotation, scheduling and replacements.
const PRELOADED_HOME_BANNER = {
  title: "TypingHub.in - India's #1 Govt Exam Typing Test Platform",
  imageUrl: '/uploads/banners/Banner_1788040851382.webp',
  targetUrl: 'https://typinghub.in',
  imageWidth: 960,
  imageHeight: 254,
  activeUntil: '2027-01-01T00:00:00.000Z',
  isPreloadedFallback: true,
};

const getPreloadedHomeBanner = () => (
  Date.now() < Date.parse(PRELOADED_HOME_BANNER.activeUntil)
    ? PRELOADED_HOME_BANNER
    : null
);

const toCachedMediaUrl = (url = '') => (
  url.startsWith('/uploads/') ? `/api/media${url}` : url
);

const responsiveBannerSources = (ad = {}) => {
  if (Array.isArray(ad.imageVariants) && ad.imageVariants.length > 0) {
    const byWidth = (width) => {
      const variant = ad.imageVariants.find(item => Number(item.width) === width);
      return variant ? {
        url: toCachedMediaUrl(variant.url),
        type: variant.type || 'image/webp',
      } : null;
    };
    return { mobile: byWidth(480), tablet: byWidth(720), desktop: byWidth(960) };
  }

  const match = ad.imageUrl?.match(/^\/uploads\/banners\/(Banner_1788040851382)\.webp$/);
  if (!match) return null;

  const base = `/api/media/uploads/banners/${match[1]}`;
  return {
    mobile: { url: `${base}-480w.avif`, type: 'image/avif' },
    tablet: { url: `${base}-720w.avif`, type: 'image/avif' },
    desktop: { url: `${base}-960w.avif`, type: 'image/avif' },
  };
};

const AdSlot = ({ type = 'banner', category, state, className = '' }) => {
  const { user } = useAuth();
  const adFree = isAdFreeUser(user);

  const [ads, setAds] = useState(() => {
    const preloadedBanner = type === 'banner' ? getPreloadedHomeBanner() : null;
    return preloadedBanner ? [preloadedBanner] : [];
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  
  const trackedImpressions = useRef(new Set());
  const timerRef = useRef(null);

  const sessionDismissKey = useMemo(() => `ad_dismissed_${type}`, [type]);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return typeof window !== 'undefined' && sessionStorage.getItem(`ad_dismissed_${type}`) === 'true';
    } catch (error) {
      return false;
    }
  });

  useEffect(() => {
    if (adFree) {
      setAds([]);
      setLoading(false);
      return undefined;
    }

    let isMounted = true;
    const fetchActiveAds = async () => {
      try {
        setLoading(true);
        const params = { type };
        if (category) params.category = category;
        if (state) params.state = state;

        const res = await api.get('/api/ads/public/active', { params });
        if (isMounted && res.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
          setAds(res.data.data);
          setCurrentIndex(0);
        } else if (isMounted) {
          setAds([]);
        }
      } catch (err) {
        if (type !== 'banner') setAds([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchActiveAds();
    return () => {
      isMounted = false;
    };
  }, [type, category, state, adFree]);

  // Track impression for current active slide
  useEffect(() => {
    if (ads.length > 0 && ads[currentIndex]) {
      const currentAd = ads[currentIndex];
      if (currentAd._id && !trackedImpressions.current.has(currentAd._id)) {
        trackedImpressions.current.add(currentAd._id);
        api.post(`/api/ads/public/${currentAd._id}/impression`).catch(() => {});
      }
    }
  }, [ads, currentIndex]);

  // Auto-rotate slides every 6 seconds if multiple ads exist
  useEffect(() => {
    if (ads.length <= 1 || isHovered) return;

    timerRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % ads.length);
    }, 6000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [ads.length, isHovered]);

  if (adFree || dismissed) {
    return null;
  }

  if (loading || ads.length === 0) {
    if (type !== 'banner') return null;
  }

  const currentAd = ads[currentIndex];
  if (!currentAd || !currentAd.imageUrl || !currentAd.targetUrl) {
    if (type !== 'banner') return null;

    // Preserve the banner slot even when no campaign is active. A future
    // campaign can then arrive from the API without moving the content below.
    return (
      <div className={`my-3.5 overflow-hidden rounded-xl md:rounded-2xl border border-slate-200/80 bg-white ${className}`} aria-hidden="true">
        <div className="min-h-[44px] px-3 flex items-center text-3xs font-bold uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-100">
          Sponsored
        </div>
        <div className="w-full aspect-[1983/524] max-h-[170px] md:max-h-[240px] bg-white" />
      </div>
    );
  }

  const fallbackDimensions = SLOT_DIMENSIONS[type] || SLOT_DIMENSIONS.banner;
  const imageWidth = Number(currentAd.imageWidth) || fallbackDimensions.width;
  const imageHeight = Number(currentAd.imageHeight) || fallbackDimensions.height;
  const imageUrl = toCachedMediaUrl(currentAd.imageUrl);
  const responsiveSources = type === 'banner'
    ? responsiveBannerSources(currentAd)
    : null;

  const handleClick = (adItem) => {
    if (adItem._id) {
      api.post(`/api/ads/public/${adItem._id}/click`).catch(() => {});
    }
  };

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDismissed(true);
    try {
      sessionStorage.setItem(sessionDismissKey, 'true');
    } catch (e) {}
  };

  const handlePrev = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? ads.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % ads.length);
  };

  // Mobile Sticky Bottom Format
  if (type === 'mobile_sticky') {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-t border-slate-200 shadow-2xl p-1.5 md:hidden transition-all duration-300">
        <div className="max-w-md mx-auto relative flex items-center justify-between gap-2">
          <a
            href={currentAd.targetUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleClick(currentAd)}
            className="flex-1 flex items-center gap-2.5 overflow-hidden"
          >
            <img
              src={imageUrl}
              alt={currentAd.title}
              width={imageWidth}
              height={imageHeight}
              loading="lazy"
              decoding="async"
              className="h-11 w-auto max-w-[120px] object-cover rounded-md shadow-2xs shrink-0"
            />
            <div className="flex-1 min-w-0">
              <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded">
                Sponsored
              </span>
              <p className="text-xs font-bold text-slate-900 truncate mt-0.5">{currentAd.title}</p>
            </div>
          </a>

          {/* Close button */}
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss banner"
            className="w-11 h-11 flex items-center justify-center text-slate-500 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 rounded-full cursor-pointer transition-colors shrink-0"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  }

  // Sidebar Format (300x250)
  if (type === 'sidebar') {
    return (
      <div 
        className={`relative my-3 overflow-hidden rounded-xl group shadow-xs border border-slate-200/80 bg-white ${className}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex items-center justify-between px-2.5 py-1 bg-slate-50 border-b border-slate-100 text-[10px] font-bold text-slate-500">
          <span>SPONSORED</span>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Close ad"
            className="w-11 h-11 -my-2 -mr-2 flex items-center justify-center text-slate-500 hover:text-slate-800 rounded cursor-pointer"
          >
            <X className="w-3 h-3" />
          </button>
        </div>

        <a
          href={currentAd.targetUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => handleClick(currentAd)}
          className="block relative w-full overflow-hidden"
        >
          <img
            src={imageUrl}
            alt={currentAd.title}
            width={imageWidth}
            height={imageHeight}
            className="w-full h-auto object-cover rounded-b-xl transition-all duration-300 group-hover:opacity-95"
            loading="lazy"
            decoding="async"
          />
        </a>

        {ads.length > 1 && (
          <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center gap-1.5 z-10 pointer-events-none">
            {ads.map((_, idx) => (
              <span
                key={idx}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  idx === currentIndex ? 'w-4 bg-blue-600' : 'w-1.5 bg-slate-300/80'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Default / Horizontal Banner Format (Leaderboard / In-Feed)
  return (
    <div 
      className={`relative my-3.5 overflow-hidden rounded-xl md:rounded-2xl group border border-slate-200/80 bg-white transition-shadow duration-200 hover:shadow-md ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="min-h-[44px] px-3 flex items-center justify-between gap-2 bg-slate-50 border-b border-slate-100">
        <span className="text-3xs font-bold uppercase tracking-wider text-slate-600">
          Sponsored
        </span>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Close banner"
          title="Dismiss ad"
          className="w-11 h-11 -my-1 -mr-2 flex items-center justify-center text-slate-600 hover:text-slate-900 hover:bg-slate-200 rounded-lg cursor-pointer transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <a
        href={currentAd.targetUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => handleClick(currentAd)}
        aria-label={`Visit sponsor: ${currentAd.title}`}
        className="block relative w-full aspect-[1983/524] max-h-[170px] md:max-h-[240px] overflow-hidden bg-white"
      >
        <picture>
          {responsiveSources && (
            <>
              {responsiveSources.mobile && (
                <source media="(max-width: 767px)" type={responsiveSources.mobile.type} srcSet={responsiveSources.mobile.url} />
              )}
              {responsiveSources.tablet && (
                <source media="(max-width: 1100px)" type={responsiveSources.tablet.type} srcSet={responsiveSources.tablet.url} />
              )}
              {responsiveSources.desktop && (
                <source type={responsiveSources.desktop.type} srcSet={responsiveSources.desktop.url} />
              )}
            </>
          )}
          <img
            src={imageUrl}
            alt={currentAd.title}
            width={imageWidth}
            height={imageHeight}
            className="w-full h-full object-cover md:object-contain mx-auto shadow-xs transition-opacity duration-300"
            loading="eager"
            decoding="sync"
            fetchpriority="high"
          />
        </picture>
      </a>

      {ads.length > 1 && isHovered && (
        <>
          <button
            type="button"
            onClick={handlePrev}
            aria-label="Previous ad"
            className="absolute left-2 top-[calc(50%+22px)] -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-black/60 hover:bg-black/80 text-white rounded-full cursor-pointer transition-colors z-20 backdrop-blur-xs"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={handleNext}
            aria-label="Next ad"
            className="absolute right-2 top-[calc(50%+22px)] -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-black/60 hover:bg-black/80 text-white rounded-full cursor-pointer transition-colors z-20 backdrop-blur-xs"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </>
      )}

      {ads.length > 1 && (
        <div className="absolute bottom-2 left-0 right-0 flex items-center justify-center gap-1.5 z-20 pointer-events-none">
          {ads.map((_, idx) => (
            <span
              key={idx}
              className={`h-1.5 rounded-full transition-all duration-300 drop-shadow ${
                idx === currentIndex ? 'w-5 bg-white' : 'w-1.5 bg-white/50'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdSlot;
